<template>
  <el-dialog :title="$t('paymentPool.edit.title')" :visible.sync="visible" width="50%" @close="handleClose">
    <el-form ref="form" :model="formData" :rules="rules" label-width="120px" class="text-left">
      <el-form-item :label="$t('paymentPool.edit.paymentName')" prop="paymentName">
        <el-input v-model="formData.paymentName" :placeholder="$t('paymentPool.edit.paymentNamePlaceholder')" />
      </el-form-item>

      <el-form-item :label="$t('paymentPool.edit.paymentType')" prop="paymentType">
        <el-select v-model="formData.paymentType" :placeholder="$t('paymentPool.edit.paymentTypePlaceholder')"
          style="width:100%" disabled>
          <el-option v-for="item in paymentTypes" :key="item.paymentType" :label="item.name"
            :value="item.paymentType" />
        </el-select>
      </el-form-item>

      <el-form-item v-for="(item, index) in paymentKeys" :key="index" :label="item.name">
        <el-input v-model="item.columnValue" type="textarea" :placeholder="item.remark" :rows="3" />
      </el-form-item>

      <el-form-item v-if="formData.paymentType === 'WECHAT'" :label="$t('paymentPool.edit.certFile')">
        <upload-file ref="uploadFile" @notify="handleNotifyCert" />
        <div v-if="formData.certPath" class="cert-tip">
          {{ $t('paymentPool.edit.currentCert') }}: {{ formData.certPath }}
        </div>
      </el-form-item>

      <el-form-item :label="$t('paymentPool.edit.payRange')" prop="payType">
        <el-select v-model="formData.payType" :placeholder="$t('paymentPool.edit.payRangePlaceholder')"
          style="width:100%" @change="handlePayTypeChange">
          <el-option :label="$t('paymentPool.edit.communityFee')" value="1001" />
          <el-option :label="$t('paymentPool.edit.tempCarFee')" value="2002" />
          <el-option :label="$t('paymentPool.edit.specifiedFee')" value="3003" />
        </el-select>
      </el-form-item>

      <el-form-item v-if="formData.payType === '3003'" :label="$t('paymentPool.edit.feeItems')">
        <el-checkbox-group v-model="formData.configIds" @change="handleConfigIdsChange">
          <el-checkbox v-for="item in feeConfigs" :key="item.configId" :label="item.configId">
            {{ item.feeName }}
            <span v-if="_boundTip(item.configId).text" class="bound-merchant"
              :class="{ 'bound-merchant-other': _boundTip(item.configId).type === 'other' }">
              （{{ _boundTip(item.configId).text }}）
            </span>
          </el-checkbox>
        </el-checkbox-group>
      </el-form-item>

      <el-form-item :label="$t('paymentPool.edit.state')" prop="state">
        <el-select v-model="formData.state" :placeholder="$t('paymentPool.edit.statePlaceholder')" style="width:100%">
          <el-option :label="$t('paymentPool.edit.enable')" value="Y" />
          <el-option :label="$t('paymentPool.edit.disable')" value="N" />
        </el-select>
      </el-form-item>

      <el-form-item :label="$t('paymentPool.edit.remark')">
        <el-input v-model="formData.remark" type="textarea" :placeholder="$t('paymentPool.edit.remarkPlaceholder')"
          :rows="3" />
      </el-form-item>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button @click="visible = false">
        {{ $t('common.cancel') }}
      </el-button>
      <el-button type="primary" @click="handleSubmit">
        {{ $t('common.confirm') }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { updatePaymentPool, listPaymentKey, listFeeConfigs, getPaymentPoolDetail, listPaymentAdapt, listPaymentPool } from '@/api/system/paymentPoolApi'
import UploadFile from '@/components/upload/uploadFile'
import { getCommunityId } from '@/api/community/communityApi'

export default {
  name: 'EditPaymentPool',
  components: {
    UploadFile
  },
  data() {
    return {
      visible: false,
      formData: {
        ppId: '',
        paymentName: '',
        paymentType: '',
        certPath: '',
        state: '',
        remark: '',
        payType: '',
        configIds: [],
        communityId: ''
      },
      paymentTypes: [],
      paymentKeys: [],
      feeConfigs: [],
      feeConfigBinds: {}, // 费用项 -> { 渠道: 已绑定商户名 }（排除自身池）
      emptyChannelKey: 'NONE', // 池 paymentType 为空时的归类键
      rules: {
        paymentName: [
          { required: true, message: this.$t('paymentPool.validate.paymentNameRequired'), trigger: 'blur' },
          { max: 64, message: this.$t('paymentPool.validate.paymentNameMaxLength'), trigger: 'blur' }
        ],
        paymentType: [
          { required: true, message: this.$t('paymentPool.validate.paymentTypeRequired'), trigger: 'change' }
        ],
        state: [
          { required: true, message: this.$t('paymentPool.validate.stateRequired'), trigger: 'change' }
        ]
      }
    }
  },
  created() {
    this.formData.communityId = getCommunityId()
    this.getPaymentTypes()
  },
  methods: {
    open(row) {
      this.visible = true
      this.formData.ppId = row.ppId
      this.formData.configIds = []
      this.getPaymentPoolDetail()
      this.getFeeConfigs()
    },
    handleClose() {
      this.$refs.form.resetFields()
      this.formData = {
        ppId: '',
        paymentName: '',
        paymentType: '',
        certPath: '',
        state: '',
        remark: '',
        payType: '',
        configIds: [],
        communityId: getCommunityId()
      }
      this.paymentKeys = []
      if (this.$refs.uploadFile) {
        this.$refs.uploadFile.clear()
      }
    },
    async getPaymentTypes() {
      try {
        const { data } = await listPaymentAdapt({
          page: 1,
          row: 100,
        })
        this.paymentTypes = data
      } catch (error) {
        console.error('获取支付类型失败:', error)
      }
    },
    async getFeeConfigs() {
      try {
        const params = {
          page: 1,
          row: 100,
          isDefault: 'F',
          communityId: this.formData.communityId
        }
        const { feeConfigs } = await listFeeConfigs(params)
        this.feeConfigs = feeConfigs
        await this._loadFeeConfigBinds()
      } catch (error) {
        console.error('获取费用配置失败:', error)
      }
    },
    /**
     * 加载费用项已绑定的收款商户信息（排除自身支付池），按渠道归类（configId -> { 渠道: 商户名 }）
     */
    async _loadFeeConfigBinds() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.formData.communityId
        }
        const res = await listPaymentPool(params)
        const pools = res.data || []
        this.feeConfigBinds = {}
        pools.forEach(pool => {
          if (pool.payType !== '3003' || pool.ppId === this.formData.ppId) return
          const _paymentTypeKey = pool.paymentType ? pool.paymentType : this.emptyChannelKey
          ;(pool.configs || []).forEach(cfg => {
            const name = pool.paymentName
            if (!this.feeConfigBinds[cfg.configId]) {
              this.feeConfigBinds[cfg.configId] = {}
            }
            const _boundName = this.feeConfigBinds[cfg.configId][_paymentTypeKey]
            if (!_boundName) {
              this.feeConfigBinds[cfg.configId][_paymentTypeKey] = name
            } else if (_boundName.indexOf(name) < 0) {
              this.feeConfigBinds[cfg.configId][_paymentTypeKey] += '、' + name
            }
          })
        })
      } catch (error) {
        console.error('获取费用项绑定商户失败:', error)
      }
    },
    /**
     * 费用项绑定提示：同渠道不可重复绑定，异渠道可并行绑定
     * @return {Object} { type: 'same' | 'other' | '', text: 提示文案 }
     */
    _boundTip(configId) {
      const _binds = this.feeConfigBinds[configId] || {}
      const _currentChannel = this.formData.paymentType || ''
      const _sameTip = []
      const _otherTip = []
      Object.keys(_binds).forEach(_key => {
        const _paymentType = _key === this.emptyChannelKey ? '' : _key
        const _channel = this._channelName(_paymentType)
        const _merchant = _binds[_key]
        if (_paymentType === _currentChannel) {
          _sameTip.push(this.$t('paymentPool.edit.boundSameChannel', { channel: _channel, merchant: _merchant }))
          return
        }
        if (!_currentChannel) {
          _sameTip.push(this.$t('paymentPool.edit.boundChannelRequired', { channel: _channel, merchant: _merchant }))
          return
        }
        _otherTip.push(this.$t('paymentPool.edit.boundOtherChannel', {
          channel: _channel,
          currentChannel: this._channelName(_currentChannel)
        }))
      })
      if (_sameTip.length > 0) {
        return { type: 'same', text: _sameTip.join('；') }
      }
      if (_otherTip.length > 0) {
        return { type: 'other', text: _otherTip.join('；') }
      }
      return { type: '', text: '' }
    },
    _channelName(paymentType) {
      if (paymentType === 'WECHAT') return this.$t('paymentPool.channel.wechat')
      if (paymentType === 'ALIPAY') return this.$t('paymentPool.channel.alipay')
      return this.$t('paymentPool.channel.other')
    },
    handleConfigIdsChange(configIds) {
      const _msgs = []
      ;(configIds || []).forEach(_configId => {
        const _tip = this._boundTip(_configId)
        if (_tip.type !== 'same' || _msgs.indexOf(_tip.text) > -1) return
        _msgs.push(_tip.text)
      })
      if (_msgs.length > 0) {
        this.$message.warning(_msgs.join('；'))
      }
    },
    async getPaymentPoolDetail() {
      try {
        const params = {
          ppId: this.formData.ppId,
          communityId: this.formData.communityId
        }
        const { data } = await getPaymentPoolDetail(params)
        this.formData = {
          ...this.formData,
          paymentName: data[0].paymentName,
          paymentType: data[0].paymentType,
          certPath: data[0].certPath,
          state: data[0].state,
          remark: data[0].remark,
          payType: data[0].payType
        }

        // 加载支付密钥
        await this.loadPaymentKeys()

        // 设置支付密钥值
        if (data[0].values) {
          data[0].values.forEach(value => {
            const key = this.paymentKeys.find(k => k.columnKey === value.columnKey)
            if (key) {
              key.columnValue = value.columnValue
            }
          })
        }

        // 设置费用项
        if (data[0].configs) {
          this.formData.configIds = data[0].configs.map(c => c.configId)
        }

        // 如果有证书路径，通知上传组件
        if (this.formData.certPath && this.$refs.uploadFile) {
          this.$refs.uploadFile.fileName = this.formData.certPath
          this.$refs.uploadFile.realFileName = this.formData.certPath
          this.$refs.uploadFile.progress = 100
        }
      } catch (error) {
        console.error('获取支付池详情失败:', error)
      }
    },
    async loadPaymentKeys() {
      try {
        const params = {
          paymentType: this.formData.paymentType,
          page: 1,
          row: 100
        }
        const { data } = await listPaymentKey(params)
        this.paymentKeys = data.map(item => ({
          ...item,
          columnValue: ''
        }))
      } catch (error) {
        console.error('获取支付密钥失败:', error)
      }
    },
    handleNotifyCert(param) {
      this.formData.certPath = param.realFileName
    },
    handlePayTypeChange() {
      if (this.formData.payType !== '3003') {
        this.formData.configIds = []
      }
    },
    handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return

        try {
          const params = {
            ...this.formData,
            paymentKeys: this.paymentKeys.map(item => ({
              columnKey: item.columnKey,
              columnValue: item.columnValue
            }))
          }
          await updatePaymentPool(params)
          this.$message.success(this.$t('common.operationSuccess'))
          this.visible = false
          this.$emit('success')
        } catch (error) {
          const _msg = error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg : (error.message || this.$t('paymentPool.edit.error'))
          this.$message.error(_msg)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.cert-tip {
  margin-top: 5px;
  font-size: 12px;
  color: #909399;
}

.bound-merchant {
  color: #f56c6c;
  font-size: 12px;
}

.bound-merchant-other {
  color: #67c23a;
}
</style>